import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api-client';

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: any) => Promise<void>>;
    __oneSignalInitialized?: boolean;
  }
}

type PermissionState = 'unknown' | 'default' | 'granted' | 'denied' | 'unsupported';

export function useOneSignal() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permissionState, setPermissionState] = useState<PermissionState>('unknown');
  const [playerId, setPlayerId] = useState<string | null>(null);
  const oneSignalRef = useRef<any>(null);

  // Read browser-level permission directly. This tells us *before* calling
  // OneSignal whether the prompt has any chance of being shown. If the user
  // previously denied (or notifications are globally off), the browser
  // returns 'denied' synchronously and the OneSignal SDK can only echo that.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof Notification === 'undefined') {
      setPermissionState('unsupported');
      return;
    }
    setPermissionState(Notification.permission as PermissionState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.__oneSignalInitialized) return;
    window.__oneSignalInitialized = true;

    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async function (OneSignal: any) {
      // Store reference for later use
      oneSignalRef.current = OneSignal;

      try {
        await OneSignal.init({
          appId: 'b9e672bc-6ecb-4b9b-9672-48794ace78f0',
          safari_web_id:
            'web.onesignal.auto.63749170-9b18-4e2b-ba12-fbd09a76fb84',
          notifyButton: { enable: false },
        });
      } catch (e: any) {
        if (!String(e?.message).includes('already initialized')) {
          console.error('OneSignal init error:', e);
          return;
        }
      }

      const supported = OneSignal.Notifications.isPushSupported();
      setIsSupported(supported);

      if (supported) {
        const sub = OneSignal.User.PushSubscription;
        const optedIn = sub.optedIn === true;
        setIsSubscribed(optedIn);
        if (optedIn && sub.id) {
          setPlayerId(sub.id);
        }
      }

      OneSignal.User.PushSubscription.addEventListener(
        'change',
        (event: any) => {
          const optedIn = event.current.optedIn === true;
          setIsSubscribed(optedIn);
          setPlayerId(optedIn ? (event.current.id || null) : null);
        },
      );
    });
  }, []);

  const requestPermission = useCallback(async (): Promise<PermissionState> => {
    const OS = oneSignalRef.current;
    if (!OS) {
      console.warn('OneSignal not initialized yet');
      return 'unknown';
    }
    // Browsers don't re-prompt once permission has been denied; calling
    // requestPermission() at that point just echoes 'denied' and OneSignal
    // logs "Permission blocked". Detect early so the UI can show meaningful
    // instructions ("you have to unblock from the browser settings").
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      setPermissionState('denied');
      return 'denied';
    }
    try {
      await OS.Notifications.requestPermission();
      await OS.User.PushSubscription.optIn();
    } catch (err) {
      // OneSignal throws "Permission blocked" when the browser-level prompt
      // can't be shown. We treat any error here as denied for UI purposes.
      console.warn('OneSignal requestPermission failed:', err);
    }
    const finalState = (typeof Notification !== 'undefined'
      ? (Notification.permission as PermissionState)
      : 'unknown');
    setPermissionState(finalState);
    return finalState;
  }, []);

  const syncToBackend = useCallback(async (apiPlayerId: string) => {
    await api.post('/users/me/push-subscription', {
      playerId: apiPlayerId,
    });
  }, []);

  const unsubscribe = useCallback(async () => {
    const OS = oneSignalRef.current;
    if (!OS) return;
    await OS.User.PushSubscription.optOut();
    try {
      await api.delete('/users/me/push-subscription');
    } catch {
      // ignore if no subscription on backend
    }
  }, []);

  return {
    isSubscribed,
    isSupported,
    permissionState,
    playerId,
    requestPermission,
    syncToBackend,
    unsubscribe,
  };
}
